/**
 * ManualSaveButton — explicit "Save draft" button shown in the workflow
 * sub-header when the user has turned autosave off.
 *
 * Hidden in all other autosave modes because the throttled write loop
 * already persists changes; surfacing a redundant button there would
 * misleadingly suggest the user *must* click it to save.
 *
 * Uses `useDraftSave` directly (rather than going through `DraftModeContext`)
 * because the sub-header is rendered above `DraftModeProvider` in the tree —
 * matches the pattern used by `DraftBadge` and `PublishButton`.
 */

import { useCallback } from 'react';
import { Button, Spinner, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { SaveRegular, SaveFilled } from '@fluentui/react-icons';
import { useDraftSave } from '@/hooks/useDraftSave';
import { useIsDraftDirty } from '@/hooks/useIsDraftDirty';
import { useDraftModeStore, selectIsDraftMode, selectDraftStatus } from '@/stores/draftModeStore';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { telemetryService } from '@/services/telemetryService';
import { getShortcutLabel } from '@/utils/input';

const useStyles = makeStyles({
  button: {
    minWidth: 'auto',
    columnGap: tokens.spacingHorizontalXS,
  },
});

export interface ManualSaveButtonProps {
  /** Workflow name to save. When omitted, the button is hidden. */
  workflowName?: string;
}

export const ManualSaveButton: React.FC<ManualSaveButtonProps> = ({ workflowName }) => {
  // Subscribe to autosave mode so we can hide ourselves when the user
  // re-enables automatic saving.
  const draftAutosaveMode = useUIPreferencesStore((s) => s.draftAutosaveMode);
  const isDraftMode = useDraftModeStore(selectIsDraftMode);

  const isVisible = draftAutosaveMode === 'off' && isDraftMode && !!workflowName;

  // Mount the inner component (which calls `useDraftSave` → React Query)
  // only when the button is actually visible. This keeps the button out of
  // the React tree — and out of dirty-state subscriptions / mutation hooks —
  // for the vast majority of sessions where autosave is on, and avoids
  // requiring a `QueryClientProvider` in light-render tests of the host
  // sub-header.
  if (!isVisible || !workflowName) {
    return null;
  }

  return <ManualSaveButtonInner workflowName={workflowName} />;
};

const ManualSaveButtonInner: React.FC<{ workflowName: string }> = ({ workflowName }) => {
  const styles = useStyles();
  const isDirty = useIsDraftDirty();
  const addNotification = useNotificationStore((s) => s.addNotification);
  // Also subscribe to the shared draft status so the button spins when a
  // save started via Ctrl/Cmd+S (or any other path that doesn't go through
  // this component's local `useDraftSave` instance).
  const storeDraftStatus = useDraftModeStore(selectDraftStatus);

  const { save, isSaving: isLocalSaving } = useDraftSave({ workflowName });
  const isSaving = isLocalSaving || storeDraftStatus === 'saving';

  const handleClick = useCallback(async () => {
    if (isSaving) return;
    telemetryService.trackEvent('workflow.draft.manual_save_clicked', {
      was_dirty: String(isDirty),
    });
    const result = await save();
    if (!result.success) {
      addNotification({
        type: 'error',
        title: 'Save failed',
        message: result.error ?? 'Unable to save draft.',
      });
    }
  }, [isSaving, isDirty, save, addNotification]);

  const isDisabled = !isDirty || isSaving;
  const tooltip = isSaving
    ? 'Saving…'
    : isDirty
      ? `Save draft (${getShortcutLabel('save')})`
      : 'No unsaved changes';

  return (
    <Tooltip content={tooltip} relationship="label">
      <Button
        className={styles.button}
        appearance="secondary"
        icon={
          isSaving ? (
            <Spinner size="tiny" />
          ) : isDirty ? (
            <SaveFilled />
          ) : (
            <SaveRegular />
          )
        }
        onClick={handleClick}
        disabled={isDisabled}
        aria-label={isSaving ? 'Saving draft' : 'Save draft'}
      >
        Save
      </Button>
    </Tooltip>
  );
};

export default ManualSaveButton;
